import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, DollarSign, ArrowRight, Star } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const resolveUrl = (url) => {
  if (!url) return "";
  if (url.includes("/api/files/") && !url.startsWith(process.env.REACT_APP_BACKEND_URL)) {
    return `${process.env.REACT_APP_BACKEND_URL}${url.substring(url.indexOf("/api/files/"))}`;
  }
  if (url.startsWith("http")) return url;
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
};

const BohoLandingPage = () => {
  const [gallery, setGallery] = useState([]);
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    axios.get(`${API}/gallery`).then(r => {
      const boho = r.data.filter(img => img.category === "braids" || img.alt?.toLowerCase().includes("boho"));
      setGallery(boho.slice(0, 6));
    }).catch((e) => console.error("Failed to load boho gallery:", e));
  }, []);

  // Countdown: always ends next Sunday 11:59 PM
  useEffect(() => {
    const getNextSunday = () => {
      const now = new Date();
      const day = now.getDay();
      const diff = day === 0 ? 7 : 7 - day;
      const next = new Date(now);
      next.setDate(now.getDate() + diff);
      next.setHours(23, 59, 59, 0);
      return next;
    };

    const target = getNextSunday();
    const timer = setInterval(() => {
      const now = new Date();
      const diff = target - now;
      if (diff <= 0) { clearInterval(timer); return; }
      setTimeLeft({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="min-h-screen bg-brand-bg" data-testid="boho-landing">
      {/* Hero */}
      <section className="relative py-20 md:py-32 bg-gradient-to-b from-brand-surface to-brand-bg">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="text-xs uppercase tracking-[0.25em] font-sans text-brand-gold mb-4">Signature Style</p>
            <h1 className="font-serif text-4xl md:text-6xl text-brand-text mb-4">
              Boho Knotless <span className="text-brand-gold">Braids</span>
            </h1>
            <p className="font-sans text-base md:text-lg text-brand-text-muted max-w-xl mx-auto mb-8">
              Effortless bohemian beauty with loose curly ends. The perfect blend of braids and curls for a free-spirited, head-turning look.
            </p>

            {/* Countdown */}
            <div className="inline-flex items-center gap-4 border border-brand-gold/30 bg-brand-surface px-6 py-4 mb-8" data-testid="boho-countdown">
              <p className="font-sans text-xs text-brand-gold font-semibold uppercase">Weekend slots fill fast — book yours:</p>
              <div className="flex gap-3">
                {[
                  { val: timeLeft.days, label: "Days" },
                  { val: timeLeft.hours, label: "Hrs" },
                  { val: timeLeft.minutes, label: "Min" },
                  { val: timeLeft.seconds, label: "Sec" },
                ].map((t) => (
                  <div key={t.label} className="text-center">
                    <p className="font-serif text-2xl text-brand-gold">{String(t.val).padStart(2, "0")}</p>
                    <p className="font-sans text-[10px] text-brand-text-muted">{t.label}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/#booking" className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-brand-gold text-black font-sans font-semibold text-sm hover:bg-brand-gold-light transition-colors" data-testid="boho-book-btn">
                Book Now — $25 Deposit <ArrowRight size={16} />
              </Link>
              <a href="tel:+13134847307" className="inline-flex items-center justify-center gap-2 px-8 py-3 border border-brand-border text-brand-text-muted font-sans text-sm hover:border-brand-gold hover:text-brand-gold transition-colors">
                Call (313) 484-7307
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16 border-t border-brand-border">
        <div className="max-w-4xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
            <div className="border border-brand-border p-6 bg-brand-surface">
              <p className="font-sans text-xs text-brand-gold mb-2 uppercase">Starting At</p>
              <p className="font-serif text-3xl text-brand-text">$210</p>
              <p className="font-sans text-xs text-brand-text-muted mt-1">$25 deposit secures your spot</p>
            </div>
            <div className="border border-brand-border p-6 bg-brand-surface">
              <p className="font-sans text-xs text-brand-gold mb-2 uppercase">Duration</p>
              <div className="flex items-center justify-center gap-2">
                <Clock size={20} className="text-brand-gold" />
                <p className="font-serif text-3xl text-brand-text">4-5</p>
              </div>
              <p className="font-sans text-xs text-brand-text-muted mt-1">hours</p>
            </div>
            <div className="border border-brand-border p-6 bg-brand-surface">
              <p className="font-sans text-xs text-brand-gold mb-2 uppercase">Lasts</p>
              <p className="font-serif text-3xl text-brand-text">6-8</p>
              <p className="font-sans text-xs text-brand-text-muted mt-1">weeks with care</p>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery */}
      {gallery.length > 0 && (
        <section className="py-16 bg-brand-surface border-t border-brand-border">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="font-serif text-2xl text-brand-text text-center mb-8">Our Boho Braids Gallery</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {gallery.map((img, i) => (
                <motion.div key={img.src || i} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.1 }} className="overflow-hidden border border-brand-border">
                  <img src={resolveUrl(img.src)} alt={img.alt} className="w-full aspect-square object-cover hover:scale-105 transition-transform duration-500" />
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Reviews */}
      <section className="py-16 border-t border-brand-border">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-serif text-2xl text-brand-text mb-8">What Clients Say</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { name: "Jasmine T.", text: "My boho braids from Afro Magic are EVERYTHING. So lightweight and natural looking!" },
              { name: "Aisha M.", text: "Best braiding salon in Southfield hands down. The boho knotless are perfect for summer." },
            ].map((r, i) => (
              <div key={r.name} className="border border-brand-border bg-brand-surface p-5 text-left">
                <div className="flex gap-1 mb-2">{[1,2,3,4,5].map(s => <Star key={s} size={14} className="text-brand-gold fill-brand-gold" />)}</div>
                <p className="font-sans text-sm text-brand-text-muted italic mb-2">"{r.text}"</p>
                <p className="font-sans text-xs text-brand-gold">— {r.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-brand-surface border-t border-brand-border">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-serif text-2xl text-brand-text mb-3">Ready for Your Boho Braids?</h2>
          <p className="font-sans text-sm text-brand-text-muted mb-6">Only $25 deposit to lock in your spot — it counts toward your total!</p>
          <Link to="/#booking" className="inline-flex items-center gap-2 px-10 py-3 bg-brand-gold text-black font-sans font-semibold text-sm hover:bg-brand-gold-light transition-colors" data-testid="boho-cta-btn">
            Book Your Boho Braids <ArrowRight size={16} />
          </Link>
          <p className="font-sans text-xs text-brand-text-muted mt-4">22560 Telegraph Rd, Southfield, MI 48033 | (313) 484-7307</p>
        </div>
      </section>
    </div>
  );
};

export default BohoLandingPage;
